import { useState, useEffect, useRef } from 'react';
import { useToast } from '../../context/ToastProvider.js';
import config from '../../constants/config.js';
import './../../styles/Modal.css';

const fontWeights = [ '100','200','300','400','500','600','700','800','900' ];
const fontStyles = [ 'normal', 'oblique', 'italic' ];
const statuses = [ 'Enabled', 'Disabled' ];

function FontEditorModal( { isOpen, onClose, onAdd, initialData } ) {
    const addToast = useToast();

    const [ fontName, setFontName ] = useState('');
    const [ fontWeight, setFontWeight ] = useState( fontWeights[3] );
    const [ fontStyle, setFontStyle ] = useState( fontStyles[0] );
    const [ status, setStatus ] = useState( statuses[0] );
    const [ fontId , setFontId ] = useState( -1 );
    const [ files, setFiles ] = useState([]);
    const [ uploading , setUploading ] = useState( false );
    const noMutation = useRef( initialData );

    useEffect(() => {
        if ( initialData != null ) {
            setFontName( initialData?.font_name || '' );
            setFontWeight( initialData?.font_weight || fontWeights[3] );
            setFontStyle( initialData?.font_style || fontStyles[0] );
            setStatus( initialData?.status || statuses[0] );
            setFiles( initialData?.files || [] );
            setFontId( initialData?.id || -1 );
        } else {
            setFontName('');
            setFontWeight( fontWeights[3] );
            setFontStyle( fontStyles[0] );
            setStatus( statuses[0] );
            setFiles( [] );
            setFontId( -1 );
        }
    }, [ initialData ] );

    function handleFileChange( e ) {
        const selectedFiles = Array.from(e.target.files);
        setFiles(prevFiles => {
            const newFiles = [...prevFiles];

            for (let i = 0; i < selectedFiles.length; i++) {
                const file = selectedFiles[i];
                let isDuplicate = false;

                for ( let j = 0; j < newFiles.length; j++ ) {
                    if ( newFiles[j].name === file.name ) {
                        isDuplicate = true;
                        break;
                    }
                }

                if ( !isDuplicate ) {
                    newFiles.push( file );
                }
            }

            return newFiles;
        });

        e.target.value = null;
    }

    function removeFile( indexToRemove ) {
        setFiles( prevFiles => {
            const newFiles = [];
            for (let i = 0; i < prevFiles.length; i++) {
                if ( i !== indexToRemove ) {
                    newFiles.push(prevFiles[i]);
                }
            }

            return newFiles;
        });
    }

    function validateFormForErrors() {
        const newErrors = {};
        if ( !fontName.trim() ) { 
            newErrors.fontName = 'Font name is required';
        };

        if ( !fontWeight ) {
            newErrors.fontWeight = 'Font weight is required';
        } 
        if ( !fontStyle ) {
            newErrors.fontStyle = 'Font style is required';
        }

        if ( !status ) { 
            newErrors.status = 'Status is required';
        }
        if ( files.length <= 0 ) {
            newErrors.files = 'At least one font file is required';
        }


        return newErrors;
    }

    async function handleSubmit() {
        setUploading( true );
        const formErrors = validateFormForErrors();
        const errorValues = Object.values( formErrors );
        const hasErrors = errorValues.length > 0;

        if ( hasErrors ) {
            console.log( formErrors );
            addToast( `Failed to save font. ${errorValues[0]}`, true , 5000 );
            setUploading( false );
            return;
        }

        const payload = {
            font_name: fontName,
            font_weight: fontWeight,
            font_style: fontStyle,
            status: status,
            files: files
        };

        if ( initialData != null ) {
            await updateFont( fontName , fontWeight , fontStyle , status , files , fontId );

            if ( typeof ( onAdd ) === 'function' ) {
                onAdd();
            }
        } else {
            await uploadFont( payload );

            if ( typeof ( onAdd ) === 'function' ) {
                onAdd();
            }
        }

        onClose();
        setUploading( false );
    }

    async function uploadFont( payload ) {
        try {
            // convert files array into binary and send to endpoint
            const filesAsBlobs = [];
            for ( let i = 0; i < payload?.files?.length; i++ ) {
                const file = payload.files[i];
                const buffer = await file.arrayBuffer();
                filesAsBlobs.push({
                    name: file.name,
                    type: file.type,
                    data: Array.from( new Uint8Array( buffer ) )
                });
            }

            payload.files = filesAsBlobs;

            const url = `${config.app.API_URL}/api/${config.app.REACT_APP_API_VERSION}/fonts`;
            const requestSettings = {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify( payload )
            };

            const response = await fetch( url , requestSettings );
            const data = await response.json();
            if ( data?.errors?.length > 0 ) {
                addToast( `Failed to add font - ${data?.errors[0].message}`, true , 5000 );
            } else {
                addToast( `Added Font "${fontName}"`, false , 5000 );
            }
        } catch ( error ) {
            console.log( error );
        }

        return;
    }

    async function updateFont( fontName , fontWeight , fontStyle , status , files , fontId ) {
        try {
            const newFiles = [];
            const removedFiles = [];
            const previousFiles = noMutation.current.files || [];
            for ( let i = 0; i < files?.length; i++ ) {
                const file = files[i];
                if ( typeof( file?.id ) === 'undefined' && file?.size > 0 ) {
                    const buffer = await file.arrayBuffer();
                    newFiles.push({
                        name: file.name,
                        type: file.type,
                        data: Array.from( new Uint8Array( buffer ) )
                    });
                }
            }

            for ( let i = 0; i < previousFiles.length; i++ ) {
                const prevFile = previousFiles[i];

                let stillExists = false;
                for ( let j = 0; j < files?.length; j++ ) {
                    const currFile = files[j];
                    if ( currFile?.id === prevFile.id ) {
                        stillExists = true;
                        break;
                    }
                }

                if ( !stillExists ) {
                    removedFiles.push( prevFile );
                }
            }

            const payload = {
                font_name: fontName,
                font_weight: fontWeight,
                font_style: fontStyle,
                status: status,
                new_files: newFiles,
                remove_files: removedFiles
            };

            const url = `${config.app.API_URL}/api/${config.app.REACT_APP_API_VERSION}/fonts/${encodeURIComponent( fontId )}`;
            const requestSettings = {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify( payload )
            };

            const response = await fetch( url , requestSettings );
            const data = await response.json();
            if ( data?.errors?.length > 0 ) {
                addToast( `Failed to update font - ${data?.errors[0].message}`, true , 5000 );
            } else {
                addToast( `Updated Font "${fontName}"`, false , 5000 );
            }
        } catch ( error ) {
            console.log( error );
        }

        return;
    }

    return(
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <button className="modal-close" onClick={onClose}>
                    <span>×</span>
                    <span className="sr-only">Close modal</span>
                </button>

                <h1>{ initialData != null ? 'Edit Font' : 'Add New Font' }</h1>

                 <div className="form-group">
                    <label>Font Name<span className="required-asterisk">*</span></label>
                    <input type="text" value={fontName} onChange={ e => setFontName(e.target.value) } />
                </div>

                <div className="form-group">
                    <label>Font Weight<span className="required-asterisk">*</span></label>
                    <select value={fontWeight} onChange={ e => setFontWeight(e.target.value) }>
                        {fontWeights.map( weight => (
                            <option key={weight} value={weight}>{weight}</option>
                        ))}
                    </select>
                </div>

                <div className="form-group">
                    <label>Font Style<span className="required-asterisk">*</span></label>
                    <select value={fontStyle} onChange={ e => setFontStyle(e.target.value) }>
                        {fontStyles.map(style => (
                        <option key={style} value={style}>{style}</option>
                        ))}
                    </select>
                </div>

                <div className="form-group">
                    <label>Status<span className="required-asterisk">*</span></label>
                    <select value={status} onChange={ e => setStatus(e.target.value) }>
                        {statuses.map(s => (
                        <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                </div>

                <div className="form-group">
                    <label>Font Files<span className="required-asterisk">*</span></label>
                    <input
                        id="file-upload"
                        type="file"
                        accept=".eot,.woff,.woff2,.ttf,.otf"
                        multiple
                        onChange={handleFileChange}
                    />

                    {files.length === 0 ? (
                        <label htmlFor="file-upload" className="custom-file-upload">
                            Choose Files
                        </label>
                    ) : (
                        <ul className="uploaded-files-list">
                            {files.map( ( file, i ) => (
                                <li key={i} className="uploaded-file-item">
                                    {file.name}
                                    <button
                                        type="button"
                                        className="remove-file-btn"
                                        onClick={() => removeFile(i)}
                                        aria-label={`Remove ${file.name}`}
                                    >
                                        ×
                                    </button>
                                </li>
                            ))}
                            <li>
                                <label htmlFor="file-upload" className="custom-file-upload add-more">
                                    Add More Files
                                </label>
                            </li>
                        </ul>
                    )}
                </div>

                <div className="modal-actions">
                    <button disabled={ uploading } onClick={onClose}>Cancel</button>
                    <button disabled={ files.length <= 0 || uploading } onClick={handleSubmit}>
                        {
                            !uploading &&
                            <span>{ initialData ? 'Update' : 'Add' }</span>
                        }
                        { uploading && 
                            <svg width="15" height="15" viewBox="0 0 50 50" className="spinner">
                                <circle cx="25" cy="25" r="20" stroke="white" strokeWidth="5" fill="none" strokeDasharray="90 150" strokeLinecap="round">
                                    <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite" />
                                </circle>
                            </svg>
                        }
                    </button>
                </div>
            </div>
        </div>
    )
}

export default FontEditorModal;